import { z } from 'zod'
import { apiErrorHandler } from '../errorHandler'
import { parseDataOfType } from '../../common/parseDataType'

// -- Agent type enum (mirrors sv_features.go FeatureXxxName constants) --

export const AgentType = {
  SearchAgent: 'SearchAgent',
  EmailAgent: 'EmailAgent',
  ScriptAgent: 'ScriptAgent',
  StoryBoardAgent: 'StoryBoardAgent',
  MarketingAgent: 'MarketingAgent'
} as const

export type AgentType = (typeof AgentType)[keyof typeof AgentType]

// -- Message author enum (mirrors llm.LlmChatRole.String() in types.go) --

export const MessageAuthor = {
  System: 'system',
  User: 'user',
  Assistant: 'assistant',
  UserMention: 'userMention'
} as const

export type MessageAuthor = (typeof MessageAuthor)[keyof typeof MessageAuthor]

// -- Shared sub-schemas --

export const TagSchema = z.object({
  tagId: z.number(),
  name: z.string(),
  colour: z.string()
})

export const AgentCompletionRefsSchema = z.object({
  DocIDs: z.array(z.number()).nullable()
})

// -- Thread list schemas (mirrors ProjectAgentThread in projects.go) --

export const ProjectAgentThreadSchema = z.object({
  projectId: z.number(),
  agent: z.enum(AgentType),
  threadId: z.number(),
  title: z.string(),
  emailFrom: z.string(),
  createdById: z.number(),
  createdBy: z.string(),
  createdOnTsMs: z.number(),
  updatedById: z.number(),
  updatedBy: z.string(),
  updatedAtTsMs: z.number(),
  userIds: z.array(z.number())
})

export type ProjectAgentThread = z.infer<typeof ProjectAgentThreadSchema>

export const GetProjectAgentThreadsResponseSchema = z.object({
  threads: z.array(ProjectAgentThreadSchema)
})

export type GetProjectAgentThreadsResponse = z.infer<typeof GetProjectAgentThreadsResponseSchema>

// -- Single thread with messages (mirrors GetThreadResponse in projects.go) --

export const ThreadMessageSchema = z.object({
  messageId: z.number(),
  author: z.enum(MessageAuthor),
  content: z.string(),
  refs: AgentCompletionRefsSchema,
  includesOrgContent: z.boolean(),
  createdById: z.number(),
  createdBy: z.string(),
  createdOnTsMs: z.number(),
  updatedById: z.number(),
  updatedBy: z.string(),
  updatedOnTsMs: z.number(),
  tags: z.array(TagSchema)
})

export type ThreadMessage = z.infer<typeof ThreadMessageSchema>

export const GetThreadResponseSchema = z.object({
  projectId: z.number(),
  threadId: z.number(),
  title: z.string(),
  emailFrom: z.string(),
  createdById: z.number(),
  createdBy: z.string(),
  createdOnTsMs: z.number(),
  updatedById: z.number(),
  updatedBy: z.string(),
  updatedOnTsMs: z.number(),
  messages: z.array(ThreadMessageSchema)
})

export type GetThreadResponse = z.infer<typeof GetThreadResponseSchema>

// -- SSE response schema (shared across all agents) --

export const AgentSseMessageType = {
  Content: 'content',
  Status: 'status',
  Error: 'error'
} as const

export const AgentSseResponseSchema = z.object({
  threadId: z.number(),
  response: z.string(),
  sseType: z.enum(AgentSseMessageType)
})

export type AgentSseResponse = z.infer<typeof AgentSseResponseSchema>

// -- New thread response (mirrors ProjectAgentNewThreadResponse in projects.go) --

export const ProjectAgentNewThreadResponseSchema = ProjectAgentThreadSchema

export type ProjectAgentNewThreadResponse = z.infer<typeof ProjectAgentNewThreadResponseSchema>

// -- Edit thread request schema (mirrors EditThreadRequest in projects.go) --

export const EditThreadRequestSchema = z.object({
  threadTitle: z.string().min(1, 'Title is required').max(200, 'Title must be 200 characters or less')
})

export type EditThreadRequest = z.infer<typeof EditThreadRequestSchema>

// -- Generic fetch functions --

export async function fetchThreads(projectId: number, agent: AgentType): Promise<GetProjectAgentThreadsResponse> {
  const res = await fetch(`/api/projects/${projectId}/threads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ agent })
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, GetProjectAgentThreadsResponseSchema)
}

export async function createThread(
  projectId: number,
  title: string,
  agent: AgentType
): Promise<ProjectAgentNewThreadResponse> {
  const res = await fetch(`/api/projects/${projectId}/threads/new`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ agent, threadTitle: title })
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, ProjectAgentNewThreadResponseSchema)
}

export async function fetchThread(projectId: number, threadId: number): Promise<GetThreadResponse> {
  const res = await fetch(`/api/projects/${projectId}/threads/${threadId}`, {
    method: 'GET',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, GetThreadResponseSchema)
}

export async function editThread(projectId: number, threadId: number, threadTitle: string): Promise<void> {
  const res = await fetch(`/api/projects/${projectId}/threads/${threadId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ threadTitle })
  })
  await apiErrorHandler(res)
}

export async function deleteThread(projectId: number, threadId: number): Promise<void> {
  const res = await fetch(`/api/projects/${projectId}/threads/${threadId}`, {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
}

export async function createThreadMessage(
  projectId: number,
  threadId: number,
  content: string,
  includeOrgContent: boolean,
  tagIds: Array<number> = []
): Promise<ThreadMessage> {
  const res = await fetch(`/api/projects/${projectId}/threads/${threadId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ content, includeOrgContent, tagIds })
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, ThreadMessageSchema)
}

export async function deleteThreadMessage(projectId: number, threadId: number, messageId: number): Promise<void> {
  const res = await fetch(`/api/projects/${projectId}/threads/${threadId}/messages/${messageId}`, {
    method: 'DELETE',
    credentials: 'same-origin'
  })
  await apiErrorHandler(res)
}
